/**
 * What a bar key press reaches (modular action bars plan, phase 3). Bar 1 reads
 * the flat `slots` list, which is its current page or the session bar that
 * replaces it. Any other bar reads the layout at its current page, and only
 * while it is shown: a hidden bar, or every extra bar during a session bar
 * (D3), casts nothing.
 */
import type { ActionBar } from '../../../shared/CharacterTypes';
import type { SlotRef } from '../../../shared/hotbar/barLayout';
import type { PageFlip } from '../../../shared/hotbar/slotActions';

export interface PressedSlot {
  abilityId?: string;
  itemId?: string;
}

export type SlotContents = { abilityId?: string; itemId?: string; type: 'ability' | 'item' | 'empty' };

export function slotAtPress(actionBar: ActionBar | undefined, bar: number, slot: number): PressedSlot | undefined {
  if (!actionBar) return undefined;
  const bar1Page = actionBar.layout?.bars.find((b) => b.id === 1)?.currentPage ?? 1;
  if (bar === 1) {
    const flat = actionBar.slots.find((s) => s.slot === slot);
    if (flat) return flat;
    return actionBar.layout?.slots.find((s) => s.bar === 1 && s.page === bar1Page && s.slot === slot);
  }
  const state = actionBar.layout?.bars.find((b) => b.id === bar);
  if (!state?.shown) return undefined;
  return actionBar.layout?.slots.find((s) => s.bar === bar && s.page === state.currentPage && s.slot === slot);
}

/** The page a flip asks for, or null when the bar is missing, not shown, or already there. */
export function pageFlipTarget(actionBar: ActionBar | undefined, flip: PageFlip): number | null {
  const state = actionBar?.layout?.bars.find((b) => b.id === flip.bar);
  if (!state?.shown) return null;
  const page = flip.to === 'next' ? (state.currentPage % state.pages) + 1
    : flip.to === 'prev' ? ((state.currentPage + state.pages - 2) % state.pages) + 1
      : flip.to;
  return page !== state.currentPage && page <= state.pages ? page : null;
}

export function resolveClientSlotRef(actionBar: ActionBar, target: number | SlotRef): SlotRef {
  if (typeof target !== 'number') return target;
  const page = actionBar.layout?.bars.find((b) => b.id === 1)?.currentPage ?? 1;
  return { bar: 1, page, slot: target };
}

export function readClientSlotContents(actionBar: ActionBar, target: number | SlotRef): SlotContents {
  const ref = resolveClientSlotRef(actionBar, target);
  const bar1Page = actionBar.layout?.bars.find((b) => b.id === 1)?.currentPage ?? 1;
  const layoutEntry = actionBar.layout?.slots.find(
    (s) => s.bar === ref.bar && s.page === ref.page && s.slot === ref.slot,
  );
  if (layoutEntry) {
    return { abilityId: layoutEntry.abilityId, itemId: layoutEntry.itemId, type: layoutEntry.type ?? 'empty' };
  }
  if (ref.bar === 1 && ref.page === bar1Page) {
    const entry = actionBar.slots.find((s) => s.slot === ref.slot);
    return { abilityId: entry?.abilityId, itemId: entry?.itemId, type: entry?.type ?? 'empty' };
  }
  return { type: 'empty' };
}

export function writeClientActionBarSlot(
  actionBar: ActionBar,
  target: number | SlotRef,
  contents: SlotContents,
): void {
  const ref = resolveClientSlotRef(actionBar, target);
  const bar1Page = actionBar.layout?.bars.find((b) => b.id === 1)?.currentPage ?? 1;
  if (ref.bar === 1 && ref.page === bar1Page) {
    const slot = actionBar.slots.find((s) => s.slot === ref.slot);
    if (slot) {
      slot.abilityId = contents.abilityId;
      slot.itemId = contents.itemId;
      slot.type = contents.type;
    } else {
      actionBar.slots.push({
        slot: ref.slot,
        ...contents,
        key: ref.slot === 10 ? '0' : ref.slot.toString(),
      });
    }
  }
  if (actionBar.layout) {
    const existing = actionBar.layout.slots.find(
      (s) => s.bar === ref.bar && s.page === ref.page && s.slot === ref.slot,
    );
    if (existing) {
      existing.abilityId = contents.abilityId;
      existing.itemId = contents.itemId;
      existing.type = contents.type;
    } else {
      actionBar.layout.slots.push({ ...ref, ...contents });
    }
  }
}

export function swapClientActionBarSlots(
  actionBar: ActionBar,
  from: number | SlotRef,
  to: number | SlotRef,
): boolean {
  const fromRef = resolveClientSlotRef(actionBar, from);
  const toRef = resolveClientSlotRef(actionBar, to);
  if (fromRef.bar === toRef.bar && fromRef.page === toRef.page && fromRef.slot === toRef.slot) {
    return false;
  }
  const moving = readClientSlotContents(actionBar, from);
  const displaced = readClientSlotContents(actionBar, to);
  writeClientActionBarSlot(actionBar, from, displaced);
  writeClientActionBarSlot(actionBar, to, moving);
  return true;
}

export function cloneActionBar(bar: ActionBar): ActionBar {
  return {
    ...bar,
    slots: bar.slots.map((s) => ({ ...s })),
    ...(bar.layout
      ? {
        layout: {
          bars: bar.layout.bars.map((b) => ({ ...b })),
          slots: bar.layout.slots.map((s) => ({ ...s })),
        },
      }
      : {}),
  };
}
